// Loyalty Points Phase 1 (Sep 2026) — the customer's redemption control.
//
// Self-contained on purpose: it fetches its own summary, owns its own
// input, and reports the chosen number of points upward. CheckoutModal
// already carries a great deal of quote/total logic, and threading
// loyalty state through it would have made both harder to follow.
//
// Everything shown here is ADVISORY. The server re-derives every ceiling
// from the authoritative order before a single point is reserved, so a
// tampered value in this component cannot spend points that aren't there
// or leave less than $1.00 payable — it just gets rejected at checkout.
//
// It renders nothing at all unless the tenant has loyalty on AND the
// caller has verified proof AND the member has enough points to reach
// the minimum, so a non-member's checkout is visually untouched.
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

// Sep 2026 (O-9) — bare `axios` with an explicit base, NOT `lib/api`.
//
// This is a PUBLIC storefront component. `lib/api` is the authenticated
// ADMIN client: it is created at module load with `axios.create()`, it
// attaches the dashboard bearer token from `localStorage`, and its 401
// handler clears that token and broadcasts `auth:session-expired`. None
// of that belongs on a customer's checkout, which authenticates with the
// `checkout_attempt_token` in the request body instead.
//
// It also broke sixteen pre-existing test suites. Every sibling in this
// folder mocks `axios` as `{ get, post }`, which is all they ever needed;
// pulling `lib/api` into this module's graph meant `axios.create` was
// suddenly called at import time and every CheckoutModal and WelcomeModal
// suite failed to load with `_axios.default.create is not a function` —
// 16 suites, 0 tests run, before a single assertion.
//
// The URL is unchanged: `lib/api` sets baseURL to `${BACKEND_URL}/api`,
// so `/public/retail/...` resolved to exactly the path below, which is
// what `routes/retail_loyalty.py` serves (`APIRouter(prefix="/api")` plus
// `/public/retail/{slug}/loyalty/...`). Note the segment order is
// `public/retail`, unlike the older `retail/public` endpoints — that is
// the backend's actual shape, not a typo.
const LOYALTY_PUBLIC_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/retail`;

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

export default function LoyaltyRedeemPanel({
  slug,
  catalogId,
  sessionId,
  verification,      // { checkout_attempt_token, destination_channel, destination_value }
  orderId,           // optional: sharpens "most you can use on this order"
  points,            // currently chosen points (lifted state)
  onChange,          // (points:number) => void
  onSummary,         // (summary) => void — lets the caller price the chosen points
}) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const token = verification?.checkout_attempt_token;

  const load = useCallback(async () => {
    // Nothing to ask for: no OTP proof, or no catalog to scope it to.
    // `catalog_id` is a REQUIRED field on the backend model, so a
    // request without one is a guaranteed 422 rather than a summary.
    //
    // Sep 2026 correction — this must also RETRACT everything derived
    // from the proof that just went away. Clearing only local `summary`
    // left the parent holding a stale summary and a stale point
    // selection, so a discounted "TOTAL DUE" survived proof expiry,
    // an identity change, New Order, and a catalog switch — and would
    // have been submitted. The retraction has to travel upward.
    if (!token || !catalogId) {
      setSummary(null);
      setError(null);
      onSummary?.(null);
      onChange?.(0);
      return;
    }
    try {
      const { data } = await axios.post(`${LOYALTY_PUBLIC_BASE}/${slug}/loyalty/summary`, {
        // Loyalty checkout visibility fix (Sep 2026) — this body must
        // carry EXACTLY the fields `routes/retail_loyalty.py::
        // LoyaltySummaryIn` declares. It inherits `_MemberAuthIn`,
        // whose `model_config = {"extra": "forbid"}` (routes/
        // retail_favorites.py) is a deliberate security control: no
        // caller may smuggle a `company_id` or any other key. That
        // model has no `session_id` field and the loyalty routes never
        // read one, so sending `session_id` made FastAPI reject EVERY
        // summary request with 422 before the handler ever ran. The
        // panel then hid itself silently (see the render gate below),
        // which is why a verified, enrolled member saw no points at
        // checkout and nothing looked broken. `sessionId` is still
        // accepted as a prop — callers pass it — it is simply not part
        // of this endpoint's contract and must not be sent.
        catalog_id: catalogId,
        checkout_attempt_token: token,
        // Sep 2026 (O-9) — `buildVerificationPayload` (lib/checkoutVerification)
        // names this key `channel`, not `destination_channel`, so reading
        // only the latter sent `undefined` on every checkout. The backend
        // treats both as OPTIONAL and falls back to resolving the member
        // from the token alone, but only once that token has already been
        // resolved and cached — so on a first use this silently cost the
        // member their redemption panel. `destination_value` stays absent
        // by design: that payload deliberately does not carry the raw
        // phone/email, and `load_verified_customer` documents the
        // token-only path as exactly equivalent in strength.
        destination_channel:
          verification?.destination_channel || verification?.channel,
        destination_value: verification?.destination_value,
        order_id: orderId || undefined,
      });
      setSummary(data);
      onSummary?.(data);
      setError(null);
      // Oct 2026 — phone-only (no-OTP) access where points cannot be
      // spent right now (number opted out of texts, or the mandatory
      // confirmation text can't be sent). Retract any chosen points so a
      // discounted total can never be submitted.
      if (data?.no_otp_redemption?.available === false) onChange?.(0);
    } catch (e) {
      const status = e?.response?.status;
      // A member who simply isn't enrolled, isn't verified, or a tenant
      // with loyalty off, is not an error the customer should ever see
      // mid-checkout — their checkout is untouched, exactly as a
      // non-member's is. 401 is `_require_verified_proof`'s refusal,
      // 403/404 the membership gate's.
      //
      // Anything else IS a fault (a 422 contract drift, a 5xx, a dead
      // network) and it must be VISIBLE. Treating every failure as
      // "quietly render nothing" is precisely what let a request that
      // could never succeed reach staging unnoticed.
      setSummary(null);
      onSummary?.(null);
      onChange?.(0);
      // 401 — the proof itself is no longer good. The member HAS a
      // token and believes they are verified, so silence would read as
      // "the restaurant lost my points". Name it and say what to do.
      // 403 (not an eligible member) and 404 (unknown storefront) stay
      // silent: there is nothing the customer can act on, and a
      // non-member's checkout must look untouched.
      // Everything else is a fault and must be visible.
      if (status === 401) setError("expired");
      else if (status === 403 || status === 404) setError(null);
      else setError("unavailable");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, catalogId, token, orderId]);

  useEffect(() => { load(); }, [load]);

  // Loyalty checkout visibility fix (Sep 2026) — a FAILED load must not
  // vanish. This notice used to live at the very bottom of the returned
  // markup, below the `return null` guard underneath it, so it was
  // unreachable by construction: every failure path sets `summary` to
  // null first and the guard returned before the notice could render.
  // A member whose points could not be loaded now sees that, in plain
  // language, with no status code, error text or stack.
  if (error === "expired") {
    return (
      <div className="text-xs text-amber-700 border-t pt-2 mt-2"
           data-testid="loyalty-verification-expired">
        Your verification expired. Verify again to use your points.
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-muted-foreground border-t pt-2 mt-2"
           data-testid="loyalty-unavailable">
        Points aren’t available right now — your order is unaffected.
      </div>
    );
  }

  if (!summary || !summary.enabled) return null;

  const available = Number(summary.available_points || 0);

  // Oct 2026 — the server decides, before checkout, whether points can be
  // spent under phone-only access, and supplies the exact wording (Product
  // Owner text for an opted-out number). The balance stays visible; the
  // points control is not offered; no OTP step is started from here.
  if (summary.no_otp_redemption && summary.no_otp_redemption.available === false) {
    return (
      <div className="text-xs border-t pt-2 mt-2 space-y-1"
           data-testid="loyalty-no-otp-redemption-unavailable">
        <div className="text-muted-foreground" data-testid="loyalty-available">
          You have {available} {available === 1 ? "point" : "points"}.
        </div>
        <div className="text-amber-700" role="status">
          {summary.no_otp_redemption.message}
        </div>
      </div>
    );
  }

  const minimum = Number(summary.min_redeem_points || 0);
  const increment = Number(summary.redeem_increment_points || 1);
  // `max_usable_points` is only meaningful once an order exists; before
  // that, fall back to what the balance alone allows.
  const cap = Number(summary.max_usable_points || 0) || available;

  if (available < minimum) {
    // Below the threshold: show progress rather than a control they
    // cannot use, so the program still feels like it is doing something.
    return (
      <div className="text-xs text-muted-foreground border-t pt-2 mt-2"
           data-testid="loyalty-below-threshold">
        You have {available} {available === 1 ? "point" : "points"}. You can start using them at{" "}
        {minimum}.
      </div>
    );
  }

  const chosen = Number(points || 0);
  const chosenValue = Math.floor(chosen / Number(summary.redeem_points || 1) || 0) *
    Number(summary.redeem_value_cents || 0);

  const setPoints = (raw) => {
    let next = Math.floor(Number(raw) || 0);
    if (next < 0) next = 0;
    if (next > cap) next = cap;
    // Snap down to a whole increment; anything else is refused server-side.
    next = Math.floor(next / increment) * increment;
    if (next > 0 && next < minimum) next = 0;
    onChange?.(next);
  };

  return (
    <div className="border-t pt-3 mt-3 space-y-2" data-testid="loyalty-redeem-panel">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">Use your points</span>
        <span className="text-muted-foreground" data-testid="loyalty-available">
          {available} available · worth {money(summary.redemption_value_cents)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {/* Sep 2026 — an accessible name. This input had only a
            `data-testid`, so a screen reader announced it as an unlabelled
            spin button and the customer had no way to know what number
            they were entering. Visually hidden rather than a visible
            label, because the panel heading above already reads "Use your
            points" to a sighted user. */}
        <label htmlFor="loyalty-points-input" className="sr-only">
          Points to use on this order
        </label>
        <input
          id="loyalty-points-input"
          type="number"
          min="0"
          max={cap}
          step={increment}
          value={chosen || ""}
          placeholder="0"
          aria-describedby="loyalty-points-help"
          onChange={(e) => setPoints(e.target.value)}
          className="w-28 rounded border px-2 py-1 text-sm"
          data-testid="loyalty-points-input"
        />
        <button
          type="button"
          onClick={() => setPoints(cap)}
          className="text-xs underline text-muted-foreground"
          data-testid="loyalty-use-max"
        >
          Use {cap}
        </button>
        {chosen > 0 && (
          <button type="button" onClick={() => setPoints(0)}
                  className="text-xs underline text-muted-foreground"
                  data-testid="loyalty-clear">
            Clear
          </button>
        )}
      </div>

      {chosen > 0 && (
        <div className="text-xs text-emerald-700" data-testid="loyalty-applied">
          {chosen} points applied — {money(chosenValue)} off what you pay today.
        </div>
      )}

      {Number(summary.estimated_points || 0) > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="loyalty-estimate">
          This order should earn about {summary.estimated_points}{" "}
          {Number(summary.estimated_points) === 1 ? "point" : "points"}.{" "}
          {summary.estimated_points_note}
        </div>
      )}

      <div id="loyalty-points-help" className="text-xs text-muted-foreground">
        At least {money(summary.min_external_payable_cents)} of every order is paid by card or
        cash.
      </div>

      {/* Sep 2026 (M-6) — pending / reconciliation indicators. Points
          already held for THIS order, and a subsystem that is temporarily
          refusing writes, are both things the customer would otherwise
          experience as an unexplained number. Neither exposes an internal
          error string or anything about another tenant. */}
      {Number(summary.points_held_for_this_order || 0) > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="loyalty-points-held">
          {summary.points_held_for_this_order} point
          {Number(summary.points_held_for_this_order) === 1 ? " is" : "s are"} already held for
          this order and will be used when you pay.
        </div>
      )}
      {summary.is_negative && (
        <div className="text-xs text-amber-700" data-testid="loyalty-negative-balance">
          A refund returned points that had already been spent, so your balance is below zero.
          You can earn back to zero — nothing is owed.
        </div>
      )}
    </div>
  );
}
